/**
 * CLM Jobs Dashboard API Client
 *
 * Provides methods for interacting with the CLM Jobs backend API
 * through the Backstage auth gateway.
 */
import {
  ApiRef,
  DiscoveryApi,
  FetchApi,
  createApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  AppliedFilters,
  ClmJobsErrorResponse,
  FilterOptions,
  JobStatisticsResponse,
  ListJobsParams,
  PageResponse,
  TranslationJob,
} from '../types';

// API path relative to the plugin base URL
const API_PATH = '/translation-jobs';
const AUTH_GATEWAY_ROUTE = '/clm-job-dashboard';

export interface ClmJobsApi {
  listJobs(params: ListJobsParams): Promise<PageResponse<TranslationJob>>;
  getJobDetails(id: string): Promise<TranslationJob>;
  getFilterOptions(): Promise<FilterOptions>;
  getJobStatistics(filters?: AppliedFilters): Promise<JobStatisticsResponse>;
  sendJobNow(id: string): Promise<void>;
  resendToCrowdin(id: string): Promise<void>;
  resendToClm(id: string): Promise<void>;
}

export const clmJobsApiRef: ApiRef<ClmJobsApi> = createApiRef<ClmJobsApi>({
  id: 'plugin.clm-jobs-dashboard.api',
});

export class ClmJobsApiClient implements ClmJobsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  /**
   * List jobs with pagination and filtering
   */
  async listJobs(
    params: ListJobsParams,
  ): Promise<PageResponse<TranslationJob>> {
    const searchParams = this.buildSearchParams(params);

    const query = searchParams.toString();
    const path = query ? `?${query}` : '';

    return this.request<PageResponse<TranslationJob>>(path);
  }

  /**
   * Get a single job by ID
   */
  async getJobDetails(id: string): Promise<TranslationJob> {
    return this.request<TranslationJob>(`/${encodeURIComponent(id)}`);
  }

  /**
   * Get filter options for populating dropdowns
   */
  async getFilterOptions(): Promise<FilterOptions> {
    return this.request<FilterOptions>('/filters');
  }

  /**
   * Get job statistics grouped by status with filters
   */
  async getJobStatistics(
    filters?: AppliedFilters,
  ): Promise<JobStatisticsResponse> {
    const searchParams = this.buildSearchParams(filters);

    const query = searchParams.toString();
    const path = query ? `/statistics?${query}` : '/statistics';

    return this.request<JobStatisticsResponse>(path);
  }

  /**
   * Send a job immediately to CLM translation
   * Only works for jobs in CREATED status with appropriate permissions
   */
  async sendJobNow(id: string): Promise<void> {
    return this.request<void>(`/${encodeURIComponent(id)}/send-now`, {
      method: 'POST',
    });
  }

  /**
   * Resend translations to Crowdin (SQS)
   * Only valid if the job is in READY_TO_EXPORT, COMPLETED, or FAILED status
   */
  async resendToCrowdin(id: string): Promise<void> {
    return this.request<void>(`/${encodeURIComponent(id)}/resend-to-crowdin`, {
      method: 'POST',
    });
  }

  /**
   * Resend job to CLM (SFTP)
   * Only valid if the job is in SUBMITTED_FOR_TRANSLATION, SUBMISSION_FAILED, or TRANSLATING status
   */
  async resendToClm(id: string): Promise<void> {
    return this.request<void>(`/${encodeURIComponent(id)}/resend-to-clm`, {
      method: 'POST',
    });
  }

  private buildSearchParams(
    filters?: AppliedFilters | ListJobsParams,
  ): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (!filters) {
      return searchParams;
    }

    this.appendArrayParams(searchParams, 'status', filters.statuses);
    this.appendArrayParams(searchParams, 'tenants', filters.tenants);
    this.appendSimpleParams(searchParams, filters);
    this.appendPaginationParams(searchParams, filters);
    this.appendSortParams(searchParams, filters);

    return searchParams;
  }

  private appendArrayParams(
    params: URLSearchParams,
    key: string,
    values?: string[],
  ): void {
    if (!values) return;
    for (const value of values) {
      params.append(key, value);
    }
  }

  private appendSimpleParams(
    params: URLSearchParams,
    filters: AppliedFilters | ListJobsParams,
  ): void {
    const mapping: Record<string, string | undefined> = {
      sourceLanguage: filters.sourceLanguage,
      targetLanguage: filters.targetLanguage,
      createdAfter: filters.createdFrom,
      createdBefore: filters.createdTo,
      query: filters.query,
    };

    for (const [key, value] of Object.entries(mapping)) {
      if (value) {
        params.set(key, value);
      }
    }
  }

  private appendPaginationParams(
    params: URLSearchParams,
    filters: AppliedFilters | ListJobsParams,
  ): void {
    if ('page' in filters && filters.page !== undefined) {
      params.set('page', String(filters.page));
    }
    if ('size' in filters && filters.size !== undefined) {
      params.set('size', String(filters.size));
    }
  }

  private appendSortParams(
    params: URLSearchParams,
    filters: AppliedFilters | ListJobsParams,
  ): void {
    if (!('sort' in filters) || !filters.sort) return;

    const [field, direction] = filters.sort.split(',');
    const sortField =
      field === 'updatedAt' ? 'LAST_MODIFIED_DATE' : 'CREATED_DATE';
    const sortDirection = direction?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
    params.set('sort', sortField);
    params.set('sortDirection', sortDirection);
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('auth-gateway');
    const url = `${baseUrl}${AUTH_GATEWAY_ROUTE}${API_PATH}${path}`;

    const response = await this.fetchApi.fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers as Record<string, string>),
      },
    });

    if (!response.ok) {
      throw await this.handleError(response);
    }

    if (response.status === 204) {
      return undefined as unknown as T;
    }

    return (await response.json()) as T;
  }

  private async handleError(response: Response): Promise<Error> {
    let errorDetail: ClmJobsErrorResponse | undefined;

    try {
      errorDetail = (await response.json()) as ClmJobsErrorResponse;
    } catch {
      // ignore parse errors
    }

    const detail = errorDetail?.error;
    const reason =
      errorDetail?.message ??
      detail?.message ??
      (response.statusText || 'CLM Jobs API request failed');

    const error = new Error(reason);

    return Object.assign(error, {
      status: errorDetail?.status ?? response.status,
      code: detail?.code,
      correlationId: detail?.correlationId,
    });
  }
}

/**
 * React hook to get the CLM Jobs API client
 */
export const useClmJobsApi = (): ClmJobsApi => {
  return useApi(clmJobsApiRef);
};
